import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { colors } from "../../helpers/styles";
import { MediumHeaderText, SmallBodyText } from "../../shared/Text";
import { Icon, PrimaryButton } from "../../shared/FormElements";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import { optionsConfig } from "./options";
import { logError } from "../../helpers/logger";
import { textWithLoadingDots, MediumBodyText } from "../../shared/Text";
import { useStateWithLocalStorageBoolean } from "../../hooks/useStateWithLocalStorage";

const VideoChat = ({
  roomName,
  password,
  displayName,
  showVideoChat,
  setShowVideoChat,
  isMobile,
  isOnRight,
  joinCall,
  setJoinCall,
  iframeClientName,
}) => {
  const [loading, setLoading] = useState(true);
  const [tileViewEnabled, setTileViewEnabled] = useStateWithLocalStorageBoolean(
    "tileViewEnabled",
    false
  );

  const jitsiRef = useRef(null);
  const passwordRef = useRef(password);
  const displayNameRef = useRef(displayName);
  const tileViewEnabledRef = useRef(tileViewEnabled);

  useEffect(() => {
    passwordRef.current = password;
  }, [password]);

  useEffect(() => {
    displayNameRef.current = displayName;
  }, [displayName]);

  useEffect(() => {
    tileViewEnabledRef.current = tileViewEnabled;
  }, [tileViewEnabled]);

  const disposeJitsi = useCallback(() => {
    const jitsi = jitsiRef.current;
    if (jitsi && typeof jitsi.dispose === "function") {
      jitsi.dispose();
    }
    jitsiRef.current = null;
  }, []);

  const executeCommand = useCallback((jitsi, command, ...args) => {
    try {
      jitsi.executeCommand(command, ...args);
    } catch (error) {
      logError(error);
    }
  }, []);

  const handleJoinCallClick = () => {
    setJoinCall(true);
    setLoading(true);
  };

  const handleDismissClick = () => {
    setShowVideoChat(false);
    setJoinCall(false);
    disposeJitsi();
  };

  useEffect(() => {
    if (isMobile || iframeClientName !== "game") {
      disposeJitsi();
      if (joinCall) {
        setJoinCall(false);
      }
      if (showVideoChat) {
        setShowVideoChat(false);
      }
    }
  }, [
    disposeJitsi,
    iframeClientName,
    isMobile,
    joinCall,
    setJoinCall,
    setShowVideoChat,
    showVideoChat,
  ]);

  useEffect(() => {
    if (
      !showVideoChat ||
      !joinCall ||
      isMobile ||
      iframeClientName !== "game"
    ) {
      disposeJitsi();
      return;
    }

    if (jitsiRef.current) {
      return;
    }

    if (!window.JitsiMeetExternalAPI) {
      setLoading(false);
      return;
    }

    const parentNode = document.getElementById("jitsi-container");
    if (!parentNode) {
      return;
    }

    let jitsi;
    try {
      jitsi = new window.JitsiMeetExternalAPI("jitsi.member.fsf.org", {
        ...optionsConfig,
        roomName,
        parentNode,
      });
      jitsiRef.current = jitsi;
    } catch (error) {
      logError(error);
      setLoading(false);
      return;
    }

    const onConferenceJoined = () => {
      if (passwordRef.current) {
        executeCommand(jitsi, "password", passwordRef.current);
      }
      if (!tileViewEnabledRef.current) {
        executeCommand(jitsi, "toggleTileView");
        tileViewEnabledRef.current = true;
        setTileViewEnabled(true);
      }
      if (displayNameRef.current) {
        executeCommand(jitsi, "displayName", displayNameRef.current);
      }
      setLoading(false);
    };

    const onPasswordRequired = () => {
      if (passwordRef.current) {
        executeCommand(jitsi, "password", passwordRef.current);
      }
      setLoading(false);
    };

    const onConferenceLeft = () => {
      setShowVideoChat(false);
      setJoinCall(false);
    };

    executeCommand(jitsi, "subject", " ");
    jitsi.addListener("videoConferenceJoined", onConferenceJoined);
    jitsi.addListener("passwordRequired", onPasswordRequired);
    jitsi.addListener("videoConferenceLeft", onConferenceLeft);

    return () => {
      if (jitsi && typeof jitsi.removeListener === "function") {
        jitsi.removeListener("videoConferenceJoined", onConferenceJoined);
        jitsi.removeListener("passwordRequired", onPasswordRequired);
        jitsi.removeListener("videoConferenceLeft", onConferenceLeft);
      }

      if (jitsiRef.current === jitsi) {
        disposeJitsi();
      } else if (jitsi && typeof jitsi.dispose === "function") {
        jitsi.dispose();
      }
    };
  }, [
    disposeJitsi,
    executeCommand,
    iframeClientName,
    isMobile,
    joinCall,
    roomName,
    setJoinCall,
    setShowVideoChat,
    setTileViewEnabled,
    showVideoChat,
  ]);

  useEffect(() => {
    if (jitsiRef.current && displayName) {
      executeCommand(jitsiRef.current, "displayName", displayName);
    }
  }, [displayName, executeCommand]);

  useEffect(() => {
    return () => {
      disposeJitsi();
    };
  }, [disposeJitsi]);

  const jitsiContainerWithLoading = (
    <>
      {loading && <VideochatLoadingWrapper>Loading</VideochatLoadingWrapper>}
      <JitsiContainer id="jitsi-container" loading={loading} />
    </>
  );

  const joinCallDisplay = (
    <JoinCallWrapper>
      <MediumHeaderText>
        <Icon icon={faVideo} color={colors.darkGrey2} isLeft={1} />
      </MediumHeaderText>
      <PrimaryButton
        style={{ margin: "0 auto" }}
        color={colors.darkGrey2}
        onClick={handleJoinCallClick}
      >
        join call
      </PrimaryButton>
      <PrimaryButton
        style={{ margin: "0 auto" }}
        color={colors.grey}
        onClick={handleDismissClick}
      >
        Dismiss
      </PrimaryButton>
      <SmallBodyText style={{ marginTop: "20px", padding: "0 10px" }}>
        Not stable on Safari or mobile yet.
      </SmallBodyText>
    </JoinCallWrapper>
  );

  const videoChatContainer = joinCall
    ? jitsiContainerWithLoading
    : joinCallDisplay;

  if (iframeClientName !== "game" || isMobile) {
    return <></>;
  }

  return (
    <>
      {!showVideoChat && (
        <WidgetWrapper onClick={() => setShowVideoChat(true)}>
          <WidgetText>
            {"<<"}
            <Icon icon={faVideo} />
          </WidgetText>
        </WidgetWrapper>
      )}
      {showVideoChat && (
        <VideoChatWrapper visible={!isMobile} isOnRight={isOnRight}>
          {videoChatContainer}
        </VideoChatWrapper>
      )}
    </>
  );
};

const VideoChatWrapper = styled.div`
  width: 15%;
  border-bottom-left-radius: 5px;
  height: 100%;
  z-index: 3;
  position: absolute;

  right: 0;
  color: ${colors.darkGrey2};
  background: ${({ theme }) => theme.foreground};
  display: ${(props) => (props.visible ? "block" : "none")};
  ${(props) => !props.isOnRight && `left: 0;`}
`;

const JoinCallWrapper = styled.div`
  padding: 10px;
  margin-top: 50px;
`;

const JitsiContainer = styled.div`
  width: 100%;
  height: 100%;
  display: ${(props) => (props.loading ? "none" : "block")};
`;

const VideochatLoadingWrapper = styled(MediumHeaderText)`
  margin-top: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  ${textWithLoadingDots}
`;

const WidgetText = styled(MediumBodyText)`
  color: ${({ theme }) => theme.text.widget};
  font-size: 14px;
  font-weight: bold;
  align-items: center;
  text-align: center;
  display: flex;
  flex-direction: column;
`;

const WidgetWrapper = styled.div`
  position: absolute;
  z-index: 3;
  cursor: pointer;
  transition: 0.3s ease padding;
  background: ${({ theme }) => theme.foreground};
  top: 40%;
  right: 0;
  border-bottom-left-radius: 7px;
  border-top-left-radius: 7px;
  padding: 5px 10px;
  overflow: hidden;
  &:hover {
    padding-right: 20px;
    opacity: 0.7;
  }
`;

export default React.memo(VideoChat);
